import ContentfulContext, { ITrendingAndNewsEntry } from 'src/contexts/ContentfulContext';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  ResourcesFilter,
  orderByDate,
  orderByText,
  sortByChronoLogicalOrder,
} from '../../components/ResourcesFilter';

import { NewsDetails } from 'src/components/LandingPage/NewsDetails';
import { TFilterDropdownOption } from 'src/components/ResourcesFilter/ResourcesFilter.types';
import { UTCMoment } from 'src/utils/UTCMoment';
import classnames from 'classnames';
// import mainImage from '../../assets/images/LandingPage/resources.png';
import s from './Trending.module.scss';

const filters = [
  {
    id: 1,
    name: 'News & Events',
  },
  {
    id: 2,
    name: 'Past Events',
  },
  {
    id: 3,
    name: 'Archived News',
  },
];

const paramsOrderBy = {
  ['Newest']: { orderBy: 'desc', type: 'createdAt' },
  ['A-Z']: { orderBy: 'asc', type: 'title' },
  ['Z-A']: { orderBy: 'desc', type: 'title' },
  ['default']: { orderBy: 'asc', type: 'date' },
};

export const Trending: React.FC = () => {
  const { trendingEntry, newsEntry } = useContext(ContentfulContext);
  const [filter, setFilter] = useState<number[]>([]);
  const [sort, setSort] = useState<TFilterDropdownOption>({ label: 'default', value: 'default' });
  const [data, setData] = useState<ITrendingAndNewsEntry[]>([]);
  const [newsSelected, setNewsSelected] = useState<ITrendingAndNewsEntry | null>(null);
  const filterMemo = useMemo(() => filter, [filter]);

  const params = useMemo(() => paramsOrderBy?.[sort?.label] || null, [sort]);

  // Initial filter
  useEffect(() => {
    setFilter([1]);
  }, [setFilter]);

  function sortBy(a, b, params, isNews = false, customSortLabel?: string | undefined) {
    switch (params?.type) {
      case 'title':
        return orderByText(a.title, b.title, params?.orderBy);
      case 'date':
        return !isNews
          ? orderByDate(
              a?.startDate || a.endDate,
              b?.startDate || b.endDate,
              customSortLabel ? customSortLabel : params?.orderBy,
            )
          : orderByDate(a?.date, b?.date, customSortLabel ? customSortLabel : params?.orderBy);
      case 'createdAt':
        return orderByDate(a.createdAt, b.createdAt, params?.orderBy);
      default:
        return 0;
    }
  }

  const trendingEntryFromToday = useMemo(() => {
    const trendingEntries = trendingEntry
      .filter((e) => !e.highlighted)
      .filter(
        (e) =>
          new Date(e?.endDate as string).getTime() >= new Date().getTime() ||
          new Date(e?.startDate as string).getTime() >= new Date().getTime(),
      )
      .sort((a, b) => sortBy(a, b, params));

    newsEntry.filter((e) => e.firstPage).forEach((entry) => trendingEntries.push(entry));
    return trendingEntries;
  }, [trendingEntry, newsEntry, params]);

  const trendingEntryFromPast = useMemo(
    () =>
      trendingEntry
        .filter((e) => !e.highlighted)
        .filter(
          (e) =>
            new Date(e?.endDate as string).getTime() < new Date().getTime() ||
            new Date(e?.startDate as string).getTime() < new Date().getTime(),
        )
        .sort((a, b) => sortBy(a, b, params)),
    [trendingEntry, params],
  );

  const trendingEntryHighlighted = useMemo(
    () =>
      trendingEntry
        .filter((e) => e.highlighted)
        .sort((a, b) => sortBy(a, b, params, false, 'desc')),
    [trendingEntry, params],
  );

  const newsEntryFiltered = useMemo(() => newsEntry.sort((a, b) => sortBy(a, b, params, true)), [
    newsEntry,
    params,
  ]);

  const applyFilter = useCallback(
    (value: number[]) => {
      let filtersData: any = [];
      let trendingHighlightedData: any = [];

      value.forEach((e) => {
        switch (e) {
          case 1:
            filtersData = [...filtersData, ...trendingEntryFromToday];
            break;
          case 2:
            filtersData = [...filtersData, ...sortByChronoLogicalOrder(trendingEntryFromPast)];
            break;
          case 3:
            filtersData = [...filtersData, ...sortByChronoLogicalOrder(newsEntryFiltered)];
            break;
          default:
            break;
        }
      });

      // ===================================================================
      // added trendings highlighted in top of list in case of filter 1 and 2
      // ===================================================================
      if (value.includes(1) || value.includes(2)) {
        trendingHighlightedData = [...trendingEntryHighlighted];
      }

      if (!value.includes(1) && (value.includes(2) || value.includes(3))) {
        const sortedEvents = sortByChronoLogicalOrder(filtersData);
        setData([...trendingHighlightedData, ...sortedEvents]);
      } else {
        setData([...trendingHighlightedData, ...filtersData]);
      }
    },
    [
      setData,
      trendingEntryFromToday,
      trendingEntryFromPast,
      newsEntryFiltered,
      trendingEntryHighlighted,
    ],
  );

  useEffect(() => {
    applyFilter(filterMemo);
  }, [filterMemo, applyFilter]);

  function redirect(link?: string) {
    if (link) {
      window.open(link, '_blank');
    }
  }

  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  function formatEvent(event: ITrendingAndNewsEntry) {
    if (event?.startDate && event?.endDate) {
      if (
        UTCMoment.utc(event.startDate).format('MMMM') ===
        UTCMoment.utc(event.endDate).format('MMMM')
      ) {
        return (
          UTCMoment.utc(event.startDate).format('DD') +
          ' - ' +
          UTCMoment.utc(event.endDate).format('DD MMMM yyyy')
        );
      } else {
        return (
          UTCMoment.utc(event.startDate).format('DD MMM') +
          ' - ' +
          UTCMoment.utc(event.endDate).format('DD MMM yyyy')
        );
      }
    }
    return UTCMoment.utc(event?.startDate || event?.date).format('DD MMMM yyyy');
  }

  function bigBox(data: ITrendingAndNewsEntry, key: number) {
    return (
      <div key={key.toString()} className={classnames(s.box, s.border)}>
        {data?.banner && <img src={data?.banner} alt="" />}
        <div className={s.boxText}>
          <h2>{data.title}</h2>
          <div className={s.dateLocal}>
            {/* <span className={s.text_large}>{formatEvent(data)}</span>
            <span className={s.text_uppercase}>
              {data.isOnline ? 'ONLINE' : data?.locale || ''}
            </span> */}
            <span className={s.eventDetail}>{capitalizeFirstLetter(data?.description)}</span>
          </div>
        </div>
        <div className={s.buttonPosition}>
          <button
            className={s.buttonApply}
            onClick={() =>
              data.type === 'news' && data?.content ? setNewsSelected(data) : redirect(data?.link)
            }>
            <span className={s.text_uppercaseButton}>See More</span>
          </button>
        </div>
      </div>
    );
  }

  function smallBox(data: ITrendingAndNewsEntry, key: number) {
    return (
      <div key={key.toString()} className={classnames(s.boxHalf, s.border)}>
        <div className={s.boxTextHalf}>
          <h2>{data.title}</h2>
          <div className={s.dateLocal}>
            <span>{formatEvent(data)}</span>
            <span className={s.textSmall}>{data.isOnline ? 'ONLINE' : data?.locale || ''}</span>
          </div>
        </div>
        <div className={s.buttonPosition}>
          <button
            className={s.buttonApply}
            onClick={() =>
              data.type === 'news' && data?.content ? setNewsSelected(data) : redirect(data?.link)
            }>
            <span className={s.text_uppercaseButton}>See More</span>
          </button>
        </div>
      </div>
    );
  }

  function topFunction() {
    const scrollToTopBtn = document.getElementById('container');
    if (scrollToTopBtn) scrollToTopBtn.scrollTop = 0;
  }

  function backFromNewsDetails() {
    setNewsSelected(null);
  }

  useEffect(() => {
    if (newsSelected) {
      topFunction();
    }
  }, [newsSelected]);

  return (
    <div id="container" className={s.container} style={newsSelected ? { paddingBottom: 0 } : {}}>
      {!newsSelected ? (
        <>
          <ResourcesFilter
            setFilter={setFilter}
            filters={filters}
            initialFilter={filter}
            setSort={setSort}
          />
          {data.map((e, key) => {
            if (e?.highlighted) {
              return bigBox(e, key);
            } else {
              return smallBox(e, key);
            }
          })}
        </>
      ) : (
        <NewsDetails back={backFromNewsDetails} data={newsSelected} />
      )}
    </div>
  );
};
